import type { ModelLayerSpecification, Map } from "@iventis/mapbox-gl";
import type { Feature, Point, Geometry } from "geojson";
import type { MapObjectProperties } from "@iventis/map-types";
import { StyleValueExtractionMethod } from "@iventis/domain-model/model/styleValueExtractionMethod";
import { getStaticStyleValue } from "@iventis/layer-style-helpers";
import type { BehaviorSubject } from "rxjs";
import type { ModelStyle } from "@iventis/domain-model/model/modelStyle";
import { DataField } from "@iventis/domain-model/model/dataField";
import { AreaModelStyle } from "@iventis/domain-model/model/areaModelStyle";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import type { ModelDataStore, ModelData } from "../../data-store/model-data-store";
import { LayerStorageScope, MapModuleLayer, MapState } from "../../types/store-schema";
import { ModelLayer } from "../generic-layers/model-layer";
import { getModelLayerStyle } from "../../utilities/layer.helpers";
import { setRemoteSourceLayer, styleValueToMapboxStyleValue } from "../mapbox/engine-mapbox-helpers";
import type { ModelLayerStyle } from "../../types/models";
import type { StylePropertyToValueMapV2 } from "../../types/style-types";
import { IMapboxLayerManager, MapboxLayerManager } from "./mapbox-layer-manager";
import { LayerSourceType } from "../../types/internal";
import { IventisMapboxLayerMetaDataUserLayer } from "../mapbox/test-helpers/mapbox-test-helper-types";

export class MapboxModelBaseLayer<
    TSource extends Feature<Geometry, MapObjectProperties>[] = Feature<Point, MapObjectProperties>[],
    TStyle extends ModelLayerStyle | AreaModelStyle = ModelStyle
> extends ModelLayer<TSource, TStyle> {
    public layerAddedToMap = false;

    protected removed = false;

    /** To align how deckgl handles rotation we need to add this value to any mapbox rotation value */
    protected readonly deckGlToMapboxRotationModifier = 180;

    /** Ensures the model will be it's original size */
    protected readonly defaultScaleValue = [1, 1, 1];

    protected readonly map: Map;

    protected readonly getAboveLayerId: (layerId: string) => string;

    protected readonly mapboxLayerManager: IMapboxLayerManager<TSource>;

    public get hasLocalLayer() {
        return this.mapboxLayerManager.hasLocalLayer;
    }

    public get hasRemoteLayer() {
        return this.mapboxLayerManager.hasRemoteLayer;
    }

    constructor(
        map: Map,
        layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        modelDataStore: ModelDataStore,
        private metaData: IventisMapboxLayerMetaDataUserLayer,
        events: {
            getAboveLayerId: (layerId: string) => string;
        }
    ) {
        super(layer, modelDataStore, state$);
        this.map = map;
        this.mapboxLayerManager = new MapboxLayerManager(map, this.layerId);
        this.getAboveLayerId = events.getAboveLayerId;
    }

    public async initaliseLayer(layer: MapModuleLayer) {
        if (layer.storageScope === LayerStorageScope.LocalOnly) {
            this.mapboxLayerManager.createLocalLayerClass();
            this.addLocalSource();
        }
        if (layer.storageScope === LayerStorageScope.LocalAndTiles) {
            this.mapboxLayerManager.createRemoteLayerClass();
        }

        await this.loadModel(layer);

        // Check if the layer has been remvoed during the time the models have been loading
        if (this.removed) {
            return;
        }

        if (layer.storageScope === LayerStorageScope.LocalOnly) {
            this.addLayer("local");
            const features = this.getGeoJson();
            if (features != null) {
                this.updateLocalSource(features);
            }
        }
        if (layer.storageScope === LayerStorageScope.LocalAndTiles) {
            this.addLayer("remote");
        }
    }

    public addLayer(layerSourceType: LayerSourceType) {
        const iventisLayer = this.getIventisLayer();
        const mapboxLayer = this.iventisLayerToMapboxLayer(
            { ...iventisLayer, id: this.layerId, source: iventisLayer.storageScope === LayerStorageScope.LocalOnly ? this.layerId : iventisLayer.source },
            this.modelData
        );
        this.mapboxLayerManager.addLayerToMapbox(mapboxLayer, { layerSourceType, aboveLayerId: this.getAboveLayerId(this.layerId) });
        this.updateMapLevel(this.state$.value.currentLevel);
        const dateFilter = this.state$.value.datesFilter.value;
        if (dateFilter.filter) {
            this.addDateFilter(iventisLayer.dataFields, dateFilter.day, dateFilter.time);
        }
        this.layerAddedToMap = true;
    }

    public removeLayer() {
        this.mapboxLayerManager.removeLayer();
        this.removed = true;
    }

    public addLocalSource() {
        this.mapboxLayerManager?.addLocalSource();
    }

    public updateLocalSource(source: TSource): void {
        this.mapboxLayerManager.updateLocalSource(source);
    }

    public removeLocalSource(): void {
        this.mapboxLayerManager.removeLocalSource();
    }

    public hideLayer(): void {
        this.mapboxLayerManager.hideLayer();
    }

    public showLayer(): void {
        this.mapboxLayerManager.showLayer();
    }

    public updateMapOrder(aboveLayerId: string): void {
        this.mapboxLayerManager.updateMapOrder(aboveLayerId);
    }

    public updateMapLevel(level: number): void {
        this.mapboxLayerManager.updateMapLevel(level);
    }

    public addDateFilter(dataFields: DataField[], day: number, time: number) {
        if (this.getIventisLayer().dataFields.some((df) => df.type === DataFieldType.RepeatedTimeRanges)) {
            this.mapboxLayerManager.addDateFilter(dataFields, day, time);
        }
    }

    public removeDateFilter() {
        this.mapboxLayerManager.removeDateFilter();
    }

    protected async loadModel(iventisLayer: MapModuleLayer) {
        await super.loadModel(iventisLayer);
        for (const model of this.modelData) {
            // Ensure the layer has not been removed before adding the model
            if (!this.removed && !this.map.hasModel(model.modelId)) {
                this.map.addModel(model.modelId, URL.createObjectURL(model.modelFileBlob));
            }
        }
    }

    public async updateStyle(change: StylePropertyToValueMapV2<TStyle>) {
        const layer = this.getIventisLayer();

        await this.loadModel(layer);

        if (change.model) {
            this.mapboxLayerManager.setLayoutProperty("model-id", this.getMapboxModelIdValue(layer));
        }
    }

    public getLayerIdsForSnapping(): string[] {
        return [this.layerId];
    }

    public iventisLayerToMapboxLayer(iventisLayer: MapModuleLayer, modelData: ModelData[]): ModelLayerSpecification {
        const mapboxLayer: ModelLayerSpecification = {
            id: iventisLayer.id,
            type: "model",
            paint: {
                "model-scale": this.getMapboxModelScaleValue(iventisLayer, modelData),
                "model-rotation": [
                    0,
                    0,
                    // Gets the rotation out of a map object property, we add the deckgl rotation modifier
                    ["+", ["get", "rotationZ"], this.deckGlToMapboxRotationModifier],
                ],
            },
            layout: {
                "model-id": this.getMapboxModelIdValue(iventisLayer),
                visibility: iventisLayer.visible ? "visible" : "none",
                "model-allow-density-reduction": false,
            },
            source: iventisLayer.source,
            metadata: this.metaData,
        };
        // Hide models when zoom level is 19 or lower for specific map/plan id
        const currentMapId = this.state$?.value?.mapId;
        if (currentMapId === "45aeda92-5737-4ff0-a113-41994b92ed60" || currentMapId === "bdc96445-d485-4b22-b24a-846c29ee605f") {
            // eslint-disable-next-line no-console
            console.log("Applying minzoom of 18 to model layer for map");
            mapboxLayer.minzoom = 18;
        }
        if (iventisLayer.storageScope === LayerStorageScope.LocalAndTiles) {
            setRemoteSourceLayer(mapboxLayer, this.layerId);
        }
        return mapboxLayer;
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    protected getMapboxModelScaleValue(iventisLayer: MapModuleLayer, modelData: ModelData[]): ModelLayerSpecification["paint"]["model-scale"] {
        return this.defaultScaleValue;
    }

    protected getMapboxModelIdValue(iventisLayer: MapModuleLayer): ModelLayerSpecification["layout"]["model-id"] {
        const { model } = getModelLayerStyle(iventisLayer);

        // Has no custom colours or images for models
        switch (model.extractionMethod) {
            case StyleValueExtractionMethod.Static:
                return getStaticStyleValue(model);
            case StyleValueExtractionMethod.Mapped:
                return styleValueToMapboxStyleValue(model);
            default:
                throw new Error(`${model.extractionMethod} is not supported for "Model" style value`);
        }
    }
}
